import type { InviteDto, MeResponse, MemberDto } from '@dbrain/shared';
import { Alert, Badge, Button, Card, EmptyState, Field, Select, TextInput } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { api } from '../api/client.js';
import { keys, useInvites, useMe, useMembers } from '../api/hooks.js';
import { useCapabilities } from '../experience.js';
import { ErrorAlert } from '../components/ErrorAlert.js';

function TenantName({ me }: { me: MeResponse }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState(me.tenant.name);
  const save = useMutation({
    mutationFn: () => api('PATCH', '/api/tenant', { name }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.me }),
  });
  return (
    <form
      className="inline-form row"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <ErrorAlert error={save.error} />
      <Field label={t('team.name')}>{({ id }) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} data-testid="tenant-name-input" />}</Field>
      <Button type="submit" busy={save.isPending} data-testid="tenant-name-save">
        {t('common.save')}
      </Button>
    </form>
  );
}

/** F6 (2026-10-30): everyone but the super admin sees the firm's name, unchangeable */
function TenantNameReadOnly({ me }: { me: MeResponse }) {
  const { t } = useTranslation();
  return (
    <Field label={t('team.name')} hint={t('team.nameSuperAdminOnly')}>
      {({ id, describedBy }) => <TextInput id={id} value={me.tenant.name} readOnly aria-describedby={describedBy} data-testid="tenant-name-readonly" />}
    </Field>
  );
}

/**
 * SA-067: before a member leaves, their workspaces and files go to another
 * member (the admin chooses; a member who leaves chooses an owner or an
 * admin). Their private conversations are not handed over.
 */
export function HandOver({ member, me, members, onCancel, onDone }: { member: MemberDto; me: MeResponse; members: MemberDto[]; onCancel: () => void; onDone?: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const self = member.userId === me.user.id;
  const candidates = members.filter((m) => m.userId !== member.userId && (!self || m.role !== 'member'));
  const [to, setTo] = useState(self ? ((candidates.find((m) => m.role === 'owner') ?? candidates[0])?.userId ?? '') : me.user.id);
  const remove = useMutation({
    mutationFn: () => api<{ transferTo: string; workspaces: number }>('DELETE', `/api/tenant/members/${member.userId}?transferTo=${to}`),
    onSuccess: async () => {
      if (self) {
        queryClient.clear();
        await queryClient.invalidateQueries({ queryKey: keys.me });
        void navigate('/');
        return;
      }
      void queryClient.invalidateQueries({ queryKey: keys.members });
      onDone?.();
    },
  });
  return (
    <div className="handover" data-testid="handover">
      <p>{self ? t('team.handover.leaveText') : t('team.handover.text', { name: member.displayName })}</p>
      <Field label={t('team.handover.to')}>
        {({ id }) => (
          <Select id={id} value={to} onChange={(e) => setTo(e.target.value)} data-testid="handover-to">
            {candidates.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.displayName} ({t(`roles.tenant.${m.role}`)})
              </option>
            ))}
          </Select>
        )}
      </Field>
      <p className="muted">{t('team.handover.private')}</p>
      <p className="muted">{t('team.handover.copies')}</p>
      <ErrorAlert error={remove.error} />
      <div className="row">
        <Button size="sm" variant="danger" busy={remove.isPending} disabled={!to} onClick={() => remove.mutate()} data-testid="handover-confirm">
          {self ? t('team.members.leave') : t('team.members.remove')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
}

export function InviteCard({ me }: { me: MeResponse }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const invites = useInvites(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const create = useMutation({
    mutationFn: () => api<InviteDto>('POST', '/api/tenant/invites', { email, role }),
    onSuccess: () => {
      setEmail('');
      void queryClient.invalidateQueries({ queryKey: keys.invites });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api('DELETE', `/api/tenant/invites/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.invites }),
  });
  return (
    <Card title={t('team.invite.title')}>
      {!me.user.emailVerified ? <Alert tone="warning" title={t('team.invite.needVerified')} /> : null}
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <ErrorAlert error={create.error} />
        {create.isSuccess ? <Alert tone="success" title={t('team.invite.sent')} /> : null}
        <Field label={t('auth.email')}>
          {({ id }) => <TextInput id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" />}
        </Field>
        <Field label={t('team.invite.role')}>
          {({ id }) => (
            <Select id={id} value={role} onChange={(e) => setRole(e.target.value as 'member' | 'admin')}>
              <option value="member">{t('roles.tenant.member')}</option>
              {me.tenant.role === 'owner' ? <option value="admin">{t('roles.tenant.admin')}</option> : null}
            </Select>
          )}
        </Field>
        <Button type="submit" busy={create.isPending} disabled={!email || !me.user.emailVerified} data-testid="invite-submit">
          {t('team.invite.submit')}
        </Button>
      </form>
      <h3>{t('team.invites.title')}</h3>
      <ErrorAlert error={invites.error ?? revoke.error} />
      {invites.data?.length === 0 ? <EmptyState>{t('team.invites.empty')}</EmptyState> : null}
      <ul className="list">
        {invites.data?.map((invite) => (
          <li key={invite.id} className="list__item">
            <span>
              {invite.email} <Badge>{t(`roles.tenant.${invite.role}`)}</Badge>
              <br />
              <span className="muted">{t('team.invites.expires', { date: new Date(invite.expiresAt).toLocaleString(i18n.language) })}</span>
            </span>
            <Button size="sm" variant="danger" onClick={() => revoke.mutate(invite.id)}>
              {t('team.invites.revoke')}
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** SA-066: anyone with a confirmed address opens an organization next to their personal space and owns it. */
export function NewOrganizationCard() {
  const { t } = useTranslation();
  const me = useMe();
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => api<MeResponse>('POST', '/api/organizations', { name: name.trim() }),
    // the session now works in the new organization: everything on the page belongs to another tenant
    onSuccess: () => window.location.assign('/admin/access'),
  });
  return (
    <Card title={t('organization.new.title')} data-testid="new-organization">
      <p className="muted">{t('organization.new.hint')}</p>
      {me.data && !me.data.user.emailVerified ? <Alert tone="warning" title={t('organization.new.needVerified')} /> : null}
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <ErrorAlert error={create.error} />
        <Field label={t('organization.new.name')}>
          {({ id }) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} data-testid="organization-name" />}
        </Field>
        <Button type="submit" busy={create.isPending} disabled={!name.trim() || !me.data?.user.emailVerified} data-testid="organization-create">
          {t('organization.new.submit')}
        </Button>
      </form>
    </Card>
  );
}

/**
 * "Firma" in the settings dialog (D-109): the organization's name (its admins change it here, in both
 * editions), the person's role and domains or teams, and for admins the way to Yönetim. A member of an
 * organization of the bireysel edition leaves it here; in the kurumsal edition membership comes from the
 * directory. Nobody lists hundreds of members here: that is Yönetim's job.
 */
export function OrganizationContent({ me }: { me: MeResponse }) {
  const { t } = useTranslation();
  const capabilities = useCapabilities();
  const admin = me.tenant.role === 'owner' || me.tenant.role === 'admin';
  const domainAdmin = Boolean(me.domains?.some((d) => d.admin));
  const personal = me.tenant.kind === 'personal';
  const [leaving, setLeaving] = useState(false);
  const members = useMembers();
  return (
    <div className="stack" data-testid="settings-organization">
      {personal ? (
        <p>
          <strong>{t('kinds.personal')}</strong>
        </p>
      ) : me.tenant.role === 'owner' ? (
        <TenantName me={me} />
      ) : (
        <TenantNameReadOnly me={me} />
      )}
      <p className="muted" data-testid="organization-role">
        {t('organization.yourRole', { role: t(`roles.tenant.${me.tenant.role}`) })}
      </p>
      {me.domains && me.domains.length > 0 ? (
        <p className="muted" data-testid="organization-domains">
          {t('organization.yourDomains', { domains: me.domains.map((d) => (d.admin ? `${d.name} (${t('admin.access.roles.domain_admin')})` : d.name)).join(', ') })}
        </p>
      ) : null}
      {capabilities.directoryLogin ? <Alert tone="info" title={t('team.directoryHint')} data-testid="directory-hint" /> : null}
      {admin || domainAdmin ? (
        <p>
          <Link className="ui-button ui-button--secondary" to="/admin" data-testid="organization-manage">
            {t('shell.management')}
          </Link>
        </p>
      ) : null}
      {!personal && !capabilities.directoryLogin && !leaving ? (
        <p>
          <Button size="sm" variant="danger" onClick={() => setLeaving(true)} data-testid="organization-leave">
            {t('team.members.leave')}
          </Button>
        </p>
      ) : null}
      {leaving && members.data ? (
        <HandOver
          member={{ userId: me.user.id, email: me.user.email, displayName: me.user.displayName, role: me.tenant.role, joinedAt: '' }}
          me={me}
          members={members.data}
          onCancel={() => setLeaving(false)}
        />
      ) : null}
      {capabilities.publicSignup ? <NewOrganizationCard /> : null}
    </div>
  );
}
